import { useEffect, useRef, useState } from "react";

import type { BuildCycle } from "@domain/entities.js";

import { continueFromDescribe, saveDescription } from "./api.js";

type SaveState = "saved" | "saving" | "error";

export interface DescribeStepProps {
  readonly tenantId: string;
  readonly cycle: BuildCycle;
  readonly onCycleChanged: (cycle: BuildCycle) => void;
  readonly onContinued: (cycle: BuildCycle) => void;
}

const AUTOSAVE_DELAY_MS = 600;

/** The one-field start promised by DSC-001. */
export const DescribeStep = ({
  tenantId,
  cycle,
  onCycleChanged,
  onContinued,
}: DescribeStepProps): React.JSX.Element => {
  const [description, setDescription] = useState(cycle.description);
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [continuing, setContinuing] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();
  const persisted = useRef(cycle.description);
  const revision = useRef(0);
  const live = useRef(true);
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const onCycleChangedRef = useRef(onCycleChanged);

  onCycleChangedRef.current = onCycleChanged;

  useEffect(() => {
    // StrictMode intentionally mounts effects twice in development. Resetting
    // here keeps the second setup live after the first setup's cleanup.
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const queueSave = (value: string, expectedRevision: number): Promise<void> => {
    setSaveState("saving");
    setFailure(undefined);

    const save = saveChain.current
      .catch(() => undefined)
      .then(async () => {
        const updated = await saveDescription(tenantId, value);
        persisted.current = updated.description;
        onCycleChangedRef.current(updated);
        if (live.current && revision.current === expectedRevision) {
          setSaveState("saved");
        }
      });

    saveChain.current = save.catch(() => undefined);
    return save.catch((error: unknown) => {
      if (live.current && revision.current === expectedRevision) {
        setSaveState("error");
        setFailure(error instanceof Error ? error.message : String(error));
      }
      throw error;
    });
  };

  useEffect(() => {
    if (description === persisted.current) return;
    const expectedRevision = revision.current;
    setSaveState("saving");
    const timer = window.setTimeout(() => {
      void queueSave(description, expectedRevision).catch(() => undefined);
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [description, tenantId]);

  const retry = (): void => {
    void queueSave(description, revision.current).catch(() => undefined);
  };

  const continueNow = async (): Promise<void> => {
    if (description.trim() === "" || continuing) return;
    setContinuing(true);
    setFailure(undefined);
    try {
      if (description !== persisted.current || saveState !== "saved") {
        await queueSave(description, revision.current);
      }
      const updated = await continueFromDescribe(tenantId);
      onContinued(updated);
    } catch (error) {
      if (live.current) {
        setFailure(error instanceof Error ? error.message : String(error));
      }
    } finally {
      if (live.current) setContinuing(false);
    }
  };

  return (
    <section aria-labelledby="describe-heading" className="panel describe">
      <h2 id="describe-heading">Describe your app</h2>
      <p>
        Say what you want to make in your own words. This is the only required
        field.
      </p>
      <label htmlFor="app-description">What should the app do?</label>
      <textarea
        id="app-description"
        rows={10}
        value={description}
        onChange={(event) => {
          revision.current += 1;
          setDescription(event.target.value);
          setFailure(undefined);
        }}
        onBlur={() => {
          // Leaving through the rail cancels the debounce timer when this
          // component unmounts. Queue the current text at blur so navigation
          // cannot turn a quick edit into lost work.
          if (description !== persisted.current) {
            void queueSave(description, revision.current).catch(() => undefined);
          }
        }}
      />

      <div className="save-status" aria-live="polite">
        {saveState === "saving" && <span>Saving…</span>}
        {saveState === "saved" && <span>Saved</span>}
        {saveState === "error" && (
          <span>
            Could not save. {" "}
            <button type="button" onClick={retry}>
              Retry
            </button>
          </span>
        )}
      </div>
      {failure && <p role="alert">{failure}</p>}

      <button
        type="button"
        className="continue"
        disabled={description.trim() === "" || continuing}
        onClick={() => void continueNow()}
      >
        {continuing ? "Continuing…" : "Continue"}
      </button>
      {description.trim() === "" && (
        <p className="blocked">Add a description to continue.</p>
      )}
    </section>
  );
};
